/* eslint-disable max-lines */
import {
  CellClassParams,
  CellClickedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  ITooltipParams,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { AsyncPipe, SlicePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  NgMultiLabelTemplateDirective,
  NgOptionComponent,
  NgOptionTemplateDirective,
  NgSelectComponent,
} from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { isArray, isObject } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';
import { SiteSelectDropdownComponent } from '@features/site-select-dropdown/site-select-dropdown.component';

import { SitesService } from '@models/sites/sites.service';

import { InputComponent } from '@shared/components/input/input.component';
import { PaginationGridComponent } from '@shared/components/pagination-grid/pagination-grid.component';
import { ProcessingLoaderComponent } from '@shared/components/processing-loader/processing-loader.component';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { initialPaginationSize } from '@shared/constants/pagination.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { AUX_ADMIN_USER } from '@shared/constants/uuid';
import { NoopValueAccessorDirective } from '@shared/directives/noop-value-accessor.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { VarDirective } from '@shared/directives/var.directive';
import { DatasourceService } from '@shared/services/datasource.service';
import {
  DocumentType,
  EntityType,
  EventType,
  listOrganizationNamesQuery,
  listSitesQuery,
  listUserNamesWithEmailQuery,
  User,
  UserAuditLog,
  UserAuditLogAction,
  UserAuditLogCategory,
} from '@shared/services/gql.service';
import { InvestigatorTransactionsService } from '@shared/services/investigator-transactions/investigator-transactions.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { QuarterCloseAdjustmentsService } from '@shared/services/quarter-close-adjustments.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { MainStore } from '@shared/store/main/main.store';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import {
  OrganizationModel,
  OrganizationStore,
} from '@shared/store/organization/organization.store';
import {
  AuxExcelStyleKeys,
  GetExcelStyle,
  ServerSideColumnFilterType,
  ServerSideFilterInfo,
  ServerSideSortOrder,
} from '@shared/utils';
import { Utils } from '@shared/utils/utils';

import { AuditHistoryExportButtonComponent } from './components/audit-history-export-button/audit-history-export-button.component';
import {
  AuditActionStatuses,
  AuditBudgetStatuses,
  AuditCategoryStatuses,
} from './constants/audit-history.constants';
import { AuditHistoryCategoryComponent } from './state/actions/audit-history-category.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProcessingLoaderComponent,
    AuditHistoryExportButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    NgSelectComponent,
    VarDirective,
    NgOptionComponent,
    NgOptionTemplateDirective,
    TooltipDirective,
    NgMultiLabelTemplateDirective,
    NoopValueAccessorDirective,
    SiteSelectDropdownComponent,
    PaginationGridComponent,
    AsyncPipe,
    SlicePipe,
  ],
  selector: 'aux-audit-history',
  styleUrls: ['audit-history.component.scss'],
  templateUrl: './audit-history.component.html',
})
export class AuditHistoryComponent implements OnDestroy {
  public organizationQuery = inject(OrganizationQuery);

  private authQuery = inject(AuthQuery);
  private datasourceService = inject(DatasourceService);
  private destroyRef = inject(DestroyRef);
  private eventService = inject(EventService);
  private formBuilder = inject(FormBuilder);
  private investigatorTransactionsService = inject(InvestigatorTransactionsService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private mainStore = inject(MainStore);
  private organizationStore = inject(OrganizationStore);
  private quarterCloseAdjustmentsService = inject(QuarterCloseAdjustmentsService);
  private router = inject(Router);
  private sitesService = inject(SitesService);
  private vendorsService = inject(OrganizationService);

  serverSideFilters: ServerSideFilterInfo<UserAuditLog>[] = [
    {
      column: 'action_text',
      inputPropertyName: 'actionSearch',
      transformFunction: (v: unknown) => (typeof v === 'string' ? v.trim() : v),
      type: ServerSideColumnFilterType.Contains,
    },
    {
      column: 'action',
      inputPropertyName: 'selectedAction',
      type: ServerSideColumnFilterType.IsEqualTo,
    },
    {
      column: 'category',
      inputPropertyName: 'selectedCategory',
      type: ServerSideColumnFilterType.IsEqualTo,
    },
    {
      column: 'created_by',
      inputPropertyName: 'selectedUser',
      type: ServerSideColumnFilterType.IsEqualTo,
    },
    {
      column: 'create_date',
      inputPropertyName: 'fromDate',
      type: ServerSideColumnFilterType.IsGreaterThanOrEqualTo,
    },
    {
      column: 'create_date',
      inputPropertyName: 'toDate',
      type: ServerSideColumnFilterType.IsLessThanOrEqualTo,
    },
    {
      column: 'vendor_id',
      inputPropertyName: 'selectedVendor',
      type: ServerSideColumnFilterType.IsEqualTo,
    },
    {
      column: 'site_id',
      inputPropertyName: 'selectedSite',
      type: ServerSideColumnFilterType.IsEqualTo,
    },
  ];
  gridFiltersFormGroup: FormGroup<{ [key: string]: FormControl<null | string | string[]> }> =
    this.formBuilder.group(
      this.serverSideFilters.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.inputPropertyName]: [],
        }),
        {}
      )
    );

  exportButton = viewChild<ExportExcelButtonComponent>('exportButton');

  _searchSubject: Subject<string> = new Subject();
  actions$ = new BehaviorSubject<{ id: UserAuditLogAction; name: string }[]>([]);
  category$ = new BehaviorSubject<{ id: UserAuditLogCategory; name: string }[]>([]);
  readonly datasource = this.datasourceService.userAuditLogDatasource;
  exportGridApi!: GridApi;
  siteNames = new Map<string, { site_name: string; site_number: string }>();
  vendorNames = new Map<string, string>();

  private readonly cellStyles = {
    display: 'block',
    'line-height': '50px',
  };

  users = new Map<string, Pick<User, 'email' | 'family_name' | 'given_name'>>();

  userFormatter = (sub: string | undefined) => {
    if (sub === AUX_ADMIN_USER) {
      return 'Auxilius System';
    }

    const user = this.users.get(sub || '');

    if (user) {
      const isUserAuxAdmin = user.email.includes('@auxili.us');

      if (this.authQuery.isAuxAdmin() || !isUserAuxAdmin) {
        return `${user.given_name} ${user.family_name}`;
      }

      return 'Auxilius Expert';
    }

    return Utils.zeroHyphen;
  };

  gridOptions: GridOptions = {
    columnDefs: [
      {
        cellRenderer: (params: ICellRendererParams) => {
          if (!params?.value) {
            this.gridAPI?.hideOverlay();

            return '<div class="h-3 m-auto spinner w-3"></div>';
          }

          return params.valueFormatted;
        },
        colId: 'date',
        field: 'date',
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
            if (cellValue < filterLocalDateAtMidnight) {
              return -1;
            }

            if (cellValue > filterLocalDateAtMidnight) {
              return 1;
            }

            return 0;
          },
          readOnly: true,
        },
        headerName: 'Date',
        minWidth: 120,
        sortable: false,
        valueFormatter: (x) => Utils.dateFormatter(x.value),
        width: 120,
      },
      {
        cellRenderer: (params: ICellRendererParams) => {
          if (!params?.value) {
            return null;
          }

          return params.valueFormatted;
        },
        colId: 'create_date',
        field: 'create_date',
        headerName: 'Time',
        minWidth: 110,
        sortable: false,
        valueFormatter: (x) => `${Utils.convertUTCToLocal(x.value)}`,
        width: 110,
      },
      {
        cellClass: 'grid-cell justify-center ',
        cellRenderer: AuditHistoryCategoryComponent,
        colId: 'category',
        field: 'category',
        headerName: 'Category',
        minWidth: 200,
        sortable: false,
        width: 200,
      },
      {
        cellClass: (params: CellClassParams) => this.getActionCellClass(params),
        cellStyle: {
          display: 'block',
          overflow: 'hidden',
          padding: 0,
          'text-overflow': 'ellipsis',
          'white-space': 'nowrap',
        },
        colId: 'action_text',
        field: 'action_text',
        headerName: 'Action',
        minWidth: 300,
        onCellClicked: (event: CellClickedEvent) => this.goToDetail(event),
        tooltipValueGetter: (params: ITooltipParams) =>
          params.data.action_text.length > 1000
            ? params.data.action_text.slice(0, 1000) + '...'
            : params.data.action_text,
      },
      {
        colId: 'action',
        field: 'action',
        hide: true,
      },
      {
        cellRenderer: (params: ICellRendererParams) => {
          if (!params?.data?.id) {
            return null;
          }

          return params.valueFormatted;
        },
        cellStyle: this.cellStyles,
        colId: 'vendor_id',
        field: 'vendor_id',
        headerName: 'Vendor Contract',
        minWidth: 120,
        valueFormatter: (x) => this.vendorNames.get(x.value) || Utils.zeroHyphen,
        width: 120,
      },
      {
        cellRenderer: (params: ICellRendererParams) => {
          if (!params?.data?.id) {
            return null;
          }

          return params.valueFormatted;
        },
        cellStyle: this.cellStyles,
        colId: 'site_id',
        field: 'site_id',
        headerName: 'Site',
        minWidth: 120,
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.siteNames.get(params.value)?.site_name;
        },
        valueFormatter: (x) => {
          return this.siteNames.get(x.value)?.site_name || Utils.zeroHyphen;
        },
        width: 120,
      },
      {
        cellRenderer: (params: ICellRendererParams) => {
          if (!params?.value) {
            return null;
          }

          return params.valueFormatted;
        },
        cellStyle: this.cellStyles,
        colId: 'created_by',
        field: 'created_by',
        headerName: 'User',
        minWidth: 110,
        valueFormatter: (x: ValueFormatterParams) => {
          return this.userFormatter(x.value);
        },
        width: 110,
      },
    ],
    defaultColDef: {
      cellClass: 'grid-cell',
      headerClass: ['bg-aux-dark-light', 'justify-center'],
      minWidth: 60,
      resizable: true,
      suppressMenu: true,
      suppressMovable: true,
    },
    groupDefaultExpanded: -1,
    overlayNoRowsTemplate:
      '<div class="text-xl">No results found, please adjust your filters</div>',
    paginationPageSize: initialPaginationSize(LocalStorageKey.PAGINATION_AUDIT_HISTORY),
    rowHeight: 50,
  };
  exportGridOptions: GridOptions = {
    ...this.gridOptions,
    excelStyles: [
      { ...GetExcelStyle(AuxExcelStyleKeys.HEADER) },
      { ...GetExcelStyle(AuxExcelStyleKeys.TEXT_AUX_ERROR) },
      { ...GetExcelStyle(AuxExcelStyleKeys.TEXT_AUX_GREEN) },
      { ...GetExcelStyle(AuxExcelStyleKeys.CELL_PERCENT) },
      { ...GetExcelStyle(AuxExcelStyleKeys.FIRST_ROW) },
    ],
    onGridReady: (event: GridReadyEvent) => {
      this.exportGridApi = event.api;
    },
  };
  filterValues$ = new BehaviorSubject<Record<string, unknown>>({});
  gridAPI?: GridApi;
  gridAPI$ = new ReplaySubject<GridApi>(1);
  loadedOnce$ = new BehaviorSubject(false);
  loading$ = new BehaviorSubject(true);
  mapActionStatus = new Map<UserAuditLogAction, string>(AuditActionStatuses);
  mapAuditCategoryStatus = new Map<UserAuditLogCategory, string>(AuditCategoryStatuses);
  optionTooltipPositions: ConnectedPosition[] = [
    {
      offsetX: -10,
      offsetY: 8,
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
  ];
  readonly paginationKey = LocalStorageKey.PAGINATION_AUDIT_HISTORY;
  processorMessage = 'This process can take up to a minute. You can navigate away from this page.';
  sortModel$ = new BehaviorSubject<Array<ServerSideSortOrder<UserAuditLog>>>([]);
  userExpertIds: string[] = [];
  users$ = new BehaviorSubject<listUserNamesWithEmailQuery[]>([]);
  vendors$ = new BehaviorSubject<OrganizationModel[]>([]);
  vendorOptions$ = this.vendors$.pipe(
    map((vendors) => {
      return vendors.length > 1 ? [{ id: '', name: 'All' }, ...vendors] : vendors;
    })
  );

  private mappedFilterValues$ = new BehaviorSubject<Record<string, unknown>>({});

  ngOnDestroy() {
    this.resetFilter();
    this.mainStore.update({ fullPage: false });
  }

  // eslint-disable-next-line max-lines-per-function
  constructor() {
    // if the selectedUser's sub is 'Auxilius' then changed that with the whole admin ids.
    this.filterValues$.pipe(takeUntilDestroyed()).subscribe((values) => {
      let selectedUser = values.selectedUser;

      if (!this.authQuery.isAuxAdmin()) {
        if (isArray(values.selectedUser)) {
          selectedUser = values.selectedUser.reduce((acc, curr) => {
            if (curr === 'Auxilius') {
              return [...acc, ...this.userExpertIds];
            }

            return [...acc, curr];
          }, []);
        }
      }

      this.mappedFilterValues$.next({
        ...values,
        selectedUser,
      });
    });
    this.mainStore.update({ fullPage: true });
    this.mainQuery
      .select('trialKey')
      .pipe(
        switchMap(() => {
          this.loading$.next(true);

          return combineLatest([
            this.mainQuery.select('userList'),
            this.vendorsService.listOrganizationNames(true),
            this.vendorsService.get(true),
            this.sitesService.get(true),
          ]);
        }),
        takeUntilDestroyed()
      )
      .subscribe(([_users, _vendors, , siteList]) => {
        this.resetFilter();
        const listAction: { id: UserAuditLogAction; name: string }[] = [];
        const listCategory: { id: UserAuditLogCategory; name: string }[] = [];

        _vendors?.data?.forEach((organization: listOrganizationNamesQuery) => {
          this.vendorNames.set(organization.id, organization.name);
        });

        siteList?.data?.forEach((site: listSitesQuery) => {
          this.siteNames.set(site.id, { site_name: site.name, site_number: site.site_no });
        });

        this.mapActionStatus.forEach((value, i) => {
          listAction.push({ id: i, name: value });
        });
        this.mapAuditCategoryStatus.forEach((value, i) => {
          listCategory.push({ id: i, name: value });
        });

        this.actions$.next(listAction.sort((x, y) => Utils.alphaNumSort(x.name, y.name)));
        this.category$.next(listCategory.sort((x, y) => Utils.alphaNumSort(x.name, y.name)));
        this.organizationStore.setActive(null);
        this.userExpertIds = [];
        _users?.forEach((user: listUserNamesWithEmailQuery) => {
          this.users.set(user.sub, user);
        });
        const vendors = this.organizationQuery.getAllVendors();

        if (vendors.length > 1) {
          this.organizationStore.setActive(null);
        }

        const sortVendors = vendors?.sort((x, y) =>
          Utils.alphaNumSort(x.name as string, y.name as string)
        );

        this.vendors$.next(sortVendors || []);

        if (_users) {
          let isExternal = false;
          const userData = _users.filter((x) => {
            if (this.userFormatter(x.sub) === 'Auxilius Expert') {
              isExternal = true;
              this.userExpertIds.push(x.sub);
            }

            return this.userFormatter(x.sub) !== 'Auxilius Expert';
          });
          const sortUsers = userData.sort((x, y) =>
            Utils.alphaNumSort(
              `${x.given_name} ${x.family_name}`,
              `${y.given_name} ${y.family_name}`
            )
          );

          if (isExternal) {
            sortUsers.unshift({
              __typename: 'User',
              email: '',
              family_name: 'Expert',
              given_name: 'Auxilius',
              sub: 'Auxilius',
            });
          }

          this.users$.next(sortUsers as listUserNamesWithEmailQuery[]);
        }

        this.loadedOnce$.next(true);
        this.loading$.next(false);
        // Sometimes vendors, sites and users data comes later than the table is drawn
        this.gridAPI?.refreshCells({ force: true });
      });
    this._searchSubject.pipe(debounceTime(500)).subscribe((value) => {
      this.gridFiltersFormGroup.get('actionSearch')?.setValue(value);
    });

    this.eventService
      .select$(EventType.REFRESH_USER_AUDIT_LOG)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.datasource.forceRefresh();
        this.loading$.next(false);
      });
  }

  firstDataRendered() {
    this.gridAPI?.sizeColumnsToFit({
      columnLimits: [
        { key: 'action_text', minWidth: 500 },
        {
          key: 'create_date',
          maxWidth: 110,
        },
        {
          key: 'date',
          maxWidth: 110,
        },
        {
          key: 'category',
          maxWidth: 170,
        },
      ],
      defaultMinWidth: 100,
    });

    this.gridAPI?.sizeColumnsToFit();
  }

  // Action text is like "Manual Override Modified - February 2021" so this method transforms the date into 2021-02-03
  formatDateFromActionText(input: string, dayOfMonth = 3): string {
    const regex = /([a-zA-Z]+)\s+(\d{4})/;
    const match = input.match(regex);

    if (match) {
      const month = match[1];
      const year = match[2];

      const date = dayjs(`${month} ${dayOfMonth}, ${year}`, ['MMM D, YYYY', 'MMMM D, YYYY']);

      return date.isValid() ? date.format('YYYY-MM-DD') : '';
    }

    return '';
  }

  getActionCellClass(item: CellClassParams) {
    const disabled = this.isActionCellDisabled(item);

    return disabled
      ? ['grid-cell', 'underline', 'cursor-not-allowed', 'opacity-50', 'aux-link']
      : ['grid-cell', 'underline', 'aux-link', 'cursor-pointer'];
  }

  getOptionName(item: unknown): string {
    if (!isObject(item)) {
      return '';
    }

    return 'name' in item ? <string>item.name : '';
  }

  // eslint-disable-next-line max-lines-per-function, complexity
  goToDetail(item: CellClickedEvent) {
    const mapBudgetStatus = new Map<UserAuditLogCategory, string>(AuditBudgetStatuses);
    const id = item.data?.id;

    if (id && !this.isActionCellDisabled(item)) {
      if (
        item.data.action === UserAuditLogAction.USER_AUDIT_LOG_ACTION_BALANCE_SHEET_ACCOUNTS_UPDATED
      ) {
        void this.router.navigateByUrl(
          `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.JOURNAL_ENTRIES}`
        );

        return;
      } else if (
        item.data.action ===
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_JOURNAL_ENTRY_CONFIGURATION_UPDATED
      ) {
        void this.router.navigateByUrl(
          `/${ROUTING_PATH.SETTINGS.INDEX}/${ROUTING_PATH.SETTINGS.CLOSE_SETTINGS.INDEX}/${ROUTING_PATH.SETTINGS.CLOSE_SETTINGS.JE_CONFIGURATION}`
        );

        return;
      } else if (
        [
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_DISCOUNT_UPDATED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_VENDOR_ESTIMATE_UPDATED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_VENDOR_ESTIMATE_UPLOADED,
        ].includes(item.data.action)
      ) {
        this.quarterCloseAdjustmentsService.updateFormControlValues(
          this.formatDateFromActionText(item.data.action_text),
          item.data.vendor_id
        );

        void this.router.navigateByUrl(
          `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.EXPENSE_DETAIL}`
        );

        return;
      } else if (
        [
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_SITE_BUDGET_AMENDMENT_CREATED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_SITE_BUDGET_AMENDMENT_INFO_UPDATED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_SITE_BUDGET_CREATED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_SITE_BUDGET_INFO_UPDATED,
        ].includes(item.data.action)
      ) {
        const detailsObject = JSON.parse(item.data.details_json);

        void this.router.navigateByUrl(
          `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}/${item.data.site_id}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.COSTS}?patient_protocol_version_id=${detailsObject.patient_protocol_version_id}&site_budget_version_id=${item.data.entity_id}`
        );

        return;
      } else if (
        [
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDER_ACTIVITY_ADDED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDER_ACTIVITY_DELETED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDER_ACTIVITY_UPDATED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDER_APPROVED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDER_ATTRIBUTE_REMOVED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDER_ATTRIBUTE_RENAMED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDER_CATEGORY_ADDED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDER_CATEGORY_UPDATED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDER_CREATED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDER_DECLINED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDER_PENDING_APPROVAL,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDER_REMOVED,
        ].includes(item.data.action)
      ) {
        const detailsObject = JSON.parse(item.data.details_json);
        const changeOrderId = detailsObject?.change_order_id || item.data.entity_id;
        const url = `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.CHANGE_ORDER.INDEX}/${changeOrderId}`;

        void this.router.navigateByUrl(url);

        return;
      } else if (
        [
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_PREPAID_ADJUSTMENT_ADDED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_PREPAID_ADJUSTMENT_DELETED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_PREPAID_ADJUSTMENT_UPDATED,
        ].includes(item.data.action)
      ) {
        void this.router.navigateByUrl(
          `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.INDEX}/${ROUTING_PATH.CLOSING.INVOICES_AND_PREPAIDS.PREPAIDS}/${item.data.vendor_id}`
        );

        return;
      } else if (
        [
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_SITE_BUDGET_AMENDMENT_COSTS_UPDATED,
          UserAuditLogAction.USER_AUDIT_LOG_ACTION_SITE_BUDGET_COSTS_UPDATED,
        ].includes(item.data.action)
      ) {
        const detailsObject = JSON.parse(item.data.details_json);

        void this.router.navigateByUrl(
          `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}/${item.data.site_id}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.COSTS}?patient_protocol_version_id=${detailsObject.patient_protocol_version_id}&site_budget_version_id=${detailsObject.site_budget_version_id}&patient_group_id=${detailsObject.patient_group_id}`
        );

        return;
      } else if (
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_SITE_BUDGET_AMENDMENT_DELETED === item.data.action
      ) {
        void this.router.navigateByUrl(
          `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}/${item.data.site_id}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.COSTS}`
        );

        return;
      } else if (
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_SCHEDULED_SITE_INVOICEABLE_GENERATED ===
        item.data.action
      ) {
        this.investigatorTransactionsService.addInMonthAdjustmentsFilters(
          this.formatDateFromActionText(item.data.action_text, 1),
          dayjs(this.formatDateFromActionText(item.data.action_text, 1))
            .endOf('month')
            .format('YYYY-MM-DD'),
          undefined,
          item.data.site_id
        );
        void this.router.navigateByUrl(
          `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_TRANSACTIONS}`
        );

        return;
      } else if (
        UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_BUDGET === item.data.category ||
        UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_FORECAST === item.data.category ||
        UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_VENDOR_ESTIMATE === item.data.category
      ) {
        this.organizationStore.setActive(item.data.vendor_id);
      } else if (
        UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_IN_MONTH_ADJUSTMENTS === item.data.category
      ) {
        this.organizationStore.setActive(item.data.vendor_id);
        void this.router.navigateByUrl(
          `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.EXPENSE_DETAIL}`
        );

        return;
      } else if (
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_TIMELINE_EVENT_QUARTER_CLOSED ===
          item.data.action ||
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_TIMELINE_EVENT_MONTH_CLOSED === item.data.action ||
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_VENDOR_ESTIMATES_LOCKED === item.data.action ||
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_VENDOR_ESTIMATES_UNLOCKED === item.data.action
      ) {
        void this.router.navigateByUrl(
          `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.EXPENSE_DETAIL}`
        );

        return;
      } else if (
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_DRIVER_LOCKED === item.data.action ||
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_DRIVER_UNLOCKED === item.data.action
      ) {
        void this.router.navigateByUrl(
          `/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.CURVES}`
        );

        return;
      } else if (
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDERS_LOCKED === item.data.action ||
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_CHANGE_ORDERS_UNLOCKED === item.data.action
      ) {
        void this.router.navigateByUrl(
          `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}`
        );

        return;
      } else if (
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_DISCOUNTS_LOCKED === item.data.action ||
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_DISCOUNTS_UNLOCKED === item.data.action
      ) {
        void this.router.navigateByUrl(
          `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.CHECKLIST}`
        );

        return;
      } else if (
        UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_EXPENSE_SOURCE === item.data.category
      ) {
        this.quarterCloseAdjustmentsService.updateFormControlValues(
          `${item.data.details?.period?.substring(0, 7)}-03`,
          item.data.vendor_id
        );
        void this.router.navigateByUrl(mapBudgetStatus.get(item.data.category) as string);

        return;
      } else if (
        UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_HISTORICAL_ADJUSTMENT === item.data.category ||
        UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_MANUAL_OVERRIDE === item.data.category
      ) {
        this.quarterCloseAdjustmentsService.updateFormControlValues(
          this.formatDateFromActionText(item.data.action_text),
          item.data.vendor_id
        );
        void this.router.navigateByUrl(mapBudgetStatus.get(item.data.category) as string);

        return;
      } else if (
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_TIMELINE_EVENT_MONTH_CLOSE_LOCKED ===
          item.data.action ||
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_TIMELINE_EVENT_MONTH_CLOSE_UNLOCKED ===
          item.data.action
      ) {
        void this.router.navigateByUrl(
          `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.EXPENSE_DETAIL}`
        );

        return;
      } else if (
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_SITE_BUDGET_UPLOADED === item.data.action
      ) {
        const dateFrom = dayjs(item.data.date);
        const dateTo = dateFrom.add(1, 'day');

        void this.router.navigateByUrl(
          `/${ROUTING_PATH.DOCUMENTS}?documentType=${DocumentType.DOCUMENT_SITE_BUDGET}&dateFrom=${dateFrom.format('YYYY-MM-DD')}&dateTo=${dateTo.format('YYYY-MM-DD')}`
        );

        return;
      } else if (
        UserAuditLogAction.USER_AUDIT_LOG_ACTION_SITE_BUDGET_AMENDMENT_UPLOADED === item.data.action
      ) {
        const dateFrom = dayjs(item.data.date);
        const dateTo = dateFrom.add(1, 'day');

        void this.router.navigateByUrl(
          `/${ROUTING_PATH.DOCUMENTS}?documentType=${DocumentType.DOCUMENT_SITE_AMENDMENT_BULK}&dateFrom=${dateFrom.format('YYYY-MM-DD')}&dateTo=${dateTo.format('YYYY-MM-DD')}`
        );

        return;
      } else if (
        UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_INVESTIGATOR_SETTINGS === item.data.category
      ) {
        void this.router.navigateByUrl(
          `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.SETTINGS.INDEX}/${ROUTING_PATH.SETTINGS.CLOSE_SETTINGS.INDEX}/${ROUTING_PATH.SETTINGS.CLOSE_SETTINGS.EXPENSE_DEFAULTS}`
        );

        return;
      }

      void this.router.navigateByUrl(mapBudgetStatus.get(item.data.category) as string);
    }
  }

  isActionCellDisabled(item: CellClassParams | CellClickedEvent): boolean {
    const allFlagsDisabled = this.launchDarklyService.$select((flags) => {
      return (
        !flags.nav_invoices &&
        !flags.nav_investigator &&
        !flags.tab_forecast_in_month &&
        !flags.nav_document_library
      );
    });
    const inMAActionArray = [
      UserAuditLogAction.USER_AUDIT_LOG_ACTION_DISCOUNT_UPDATED,
      UserAuditLogAction.USER_AUDIT_LOG_ACTION_TIMELINE_EVENT_MONTH_CLOSE_LOCKED,
      UserAuditLogAction.USER_AUDIT_LOG_ACTION_TIMELINE_EVENT_MONTH_CLOSE_UNLOCKED,
      UserAuditLogAction.USER_AUDIT_LOG_ACTION_TIMELINE_EVENT_QUARTER_CLOSED,
      UserAuditLogAction.USER_AUDIT_LOG_ACTION_TIMELINE_EVENT_MONTH_CLOSED,
      UserAuditLogAction.USER_AUDIT_LOG_ACTION_VENDOR_ESTIMATES_LOCKED,
      UserAuditLogAction.USER_AUDIT_LOG_ACTION_VENDOR_ESTIMATES_UNLOCKED,
      UserAuditLogAction.USER_AUDIT_LOG_ACTION_VENDOR_ESTIMATE_UPDATED,
      UserAuditLogAction.USER_AUDIT_LOG_ACTION_VENDOR_ESTIMATE_UPLOADED,
    ];

    const inMACategoryArr = [UserAuditLogCategory.USER_AUDIT_LOG_CATEGORY_MANUAL_OVERRIDE];

    return (
      allFlagsDisabled() &&
      (inMAActionArray.includes(item.data.action) || inMACategoryArr.includes(item.data.category))
    );
  }

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;
    this.datasource.initialize({
      filters: this.serverSideFilters,
      filterValues$: this.mappedFilterValues$,
      parseFunction: (items) => {
        return items?.map((x) => {
          return {
            ...x,
            date: Utils.dateParse(x.create_date),
            details: (x.details_json ? JSON.parse(x.details_json) : undefined) || {},
          };
        });
      },
      sortModel$: this.sortModel$,
      untilDestroyedPipeOperator: takeUntilDestroyed(this.destroyRef),
    });
  }

  onMultiselectChange(controlValue: OrganizationModel | string, controlName: string): void {
    if (controlValue === '' || (typeof controlValue !== 'string' && controlValue.id === '')) {
      this.gridFiltersFormGroup.get(controlName)?.patchValue('');
    }
  }

  resetFilter() {
    this.gridFiltersFormGroup.reset();
  }

  async triggerFetchAuditHistory() {
    this.loading$.next(true);

    await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getValue().trialKey,
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({}),
        type: EventType.USER_AUDIT_LOG,
      })
    ).finally(() => {
      setTimeout(() => {
        // if we are still showing the loading spinner after 5 minutes (300000 milliseconds),
        // set loading to false. If the user audit log process fails for whatever reason,
        // we don't want this to spin forever
        if (this.loading$.getValue()) {
          this.loading$.next(false);
        }
      }, 300000);
    });
  }

  // updateSearch(target: EventTarget | null): void {
  //   const searchText = target as HTMLInputElement;
  updateSearch(target: EventTarget | null) {
    const searchTextValue = target as HTMLInputElement;

    if (searchTextValue.value.length > 2 || searchTextValue.value.length === 0) {
      // todo(upgrade) find out why we are looking at this key value should be string
      this._searchSubject.next(searchTextValue.value);
    }
  }
}
